import React, { useEffect, useState, useRef } from "react";
import { useSelector, shallowEqual } from "react-redux";
import * as apiLearning from "../../../api/learning";

function VideoThumb({ url }) {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const onMeta = () => {
      try {
        v.currentTime = 10.0;
      } catch (error) {
        console.error("Impossibile posizionare l'anteprima video", error);
      }
    };
    v.addEventListener("loadedmetadata", onMeta);
    return () => {
      v.removeEventListener("loadedmetadata", onMeta);
    };
  }, [url]);
  return (
    <video ref={ref} src={url} muted playsInline preload="metadata" className="tw:object-cover tw:w-full tw:h-full" />
  );
}

function VideoCard({ video, onClick, isCompleted }) {
  const { user } = useSelector(({ auth }) => auth, shallowEqual);
  // Prima di procedere aspetta i risultati delle promise
  const token = user?.authToken;

  const [quizId, setQuizId] = useState(0);
  const [percent, setPercent] = useState(0.0);

  useEffect(() => {
    let active = true;
    setQuizId(0);
    if (!token) return;
    apiLearning
      .getQuizByVideo(token, video.video_id)
      .then((data) => {
        if (active) setQuizId(data?.quiz?.quiz_id || 0);
      })
      .catch((error) => console.error("Caricamento quiz fallito", error));
    return () => {
      active = false;
    };
  }, [token, video.video_id]);

  useEffect(() => {
    let active = true;
    if (!token || !quizId) {
      setPercent(0.0);
      return () => {
        active = false;
      };
    }
    apiLearning
      .getQuizResults(token, { videoId: video.video_id, quizId })
      .then((data) => {
        if (!active) return;
        if (data[0]?.percent != null) {
          setPercent(parseFloat(data[0].percent) || 0.0);
        } else {
          setPercent(0.0);
        }
      })
      .catch((error) => console.error("Caricamento risultato quiz fallito", error));
    return () => {
      active = false;
    };
  }, [token, video.video_id, quizId]);

  return (
    <button
      type="button"
      className={`tw:text-left tw:bg-white tw:rounded tw:shadow tw:overflow-hidden tw:border tw:border-transparent tw:hover:border-blue-400 tw:flex tw:flex-col tw:w-full ${
        isCompleted ? "tw:opacity-90" : ""
      }`}
      onClick={onClick}
    >
      <div className="tw:w-full tw:aspect-video tw:bg-black tw:relative" style={{ aspectRatio: "16/9" }}>
        <VideoThumb url={video.url} />
        {isCompleted && (
          <div className="tw:absolute tw:inset-0 tw:bg-black/40 tw:flex tw:items-center tw:justify-center tw:backdrop-blur-[1px]">
            <span
              className={`
                ${percent < 80.0 ? "tw:bg-amber-200" : "tw:bg-green-600"} 
                ${percent < 80.0 ? "tw:text-amber-600" : "tw:text-white"} 
                ${percent < 80.0 ? "tw:font-bold" : "tw:font-bold"} tw:px-3 tw:py-1 tw:rounded-full tw:text-sm tw:shadow-lg tw:flex tw:items-center tw:gap-1`}
            >
              <svg className="tw:w-4 tw:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {percent < 80.0 ? "Non superato" : "Completato "}
            </span>
          </div>
        )}
      </div>
      <div className="tw:p-4 tw:space-y-1 tw:flex-1 tw:w-full">
        <div className="tw:text-base tw:font-semibold tw:truncate tw:w-full" title={video.title}>
           <span
              className={`${percent < 80.0 ? "tw:text-amber-600" : "tw:text-green-800"} `}>
              Superato al {percent.toFixed(0.0)}%
            </span>
        </div>
        <div className="tw:text-base tw:font-semibold tw:truncate tw:w-full" title={video.title}>
          {video.title}
        </div>
        <div className="tw:text-xs tw:text-gray-600">ID: {video.video_id}</div>
        {video.duration_sec ? <div className="tw:text-xs tw:text-gray-600">Durata: {video.duration_sec}s</div> : null}
      </div>
    </button>
  );
}

export default function VideosList({ onSelect }) {
  const { authToken } = useSelector(({ auth }) => auth, shallowEqual);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTodo, setShowTodo] = useState(true); // 👈 espanso di default
  const [showDone, setShowDone] = useState(false); // 👈 compresso di default

  const handleSelect = (v) => {
    if (!authToken) return;
    const payload = { id: v.video_id, title: v.title, url: v.url, durationSec: v.duration_sec, targets: [] };
    if (typeof onSelect === "function") onSelect(payload);
  };

  useEffect(() => {
    let active = true;
    if (!authToken) return;
    setLoading(true);
    apiLearning
      .getAvailableVideos(authToken)
      .then((data) => {
        if (active) setVideos(Array.isArray(data) ? data : []);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [authToken]);

  const isCompleted = (v) => {
    const vidDone = parseInt(v.is_video_completed || 0) === 1;
    const hasQuiz = parseInt(v.has_quiz || 0) > 0;
    const quizDone = parseInt(v.quiz_passed || 0) > 0;

    if (!vidDone) return false;
    if (hasQuiz && !quizDone) return false;

    return true;
  };

  const todoVideos = videos.filter((v) => !isCompleted(v));
  const doneVideos = videos.filter((v) => isCompleted(v));

  return (
    <div className="tw:container tw:mx-auto tw:p-6 tw:max-w-7xl">
      {loading ? (
        <div className="tw:flex tw:justify-center tw:p-12">
          <div className="tw:animate-spin tw:rounded-full tw:h-12 tw:w-12 tw:border-b-2 tw:border-blue-600"></div>
        </div>
      ) : videos.length === 0 ? (
        <div className="tw:p-6 tw:bg-yellow-50 tw:text-yellow-800 tw:rounded-lg tw:border tw:border-yellow-200">
          Nessun video disponibile per il tuo target.
        </div>
      ) : (
        <div className="tw:space-y-12">
          {/* Video Da Completare */}
          <div>
            <div
              className="tw:p-3 tw:flex tw:items-center tw:gap-3 tw:mb-3 tw:cursor-pointer"
              onClick={() => setShowTodo(!showTodo)}
            >
              <h2 className="tw:text-xl tw:font-bold tw:text-gray-800">
                <span className="tw:bg-blue-500 tw:text-white tw:mr-2 tw:px-4 tw:py-1 tw:rounded-full tw:text-xl tw:font-bold tw:shadow-sm">
                  {todoVideos.length}
                </span>
                Video Disponibili
              </h2>
              <svg
                className={`tw:w-6 tw:h-6 tw:ml-auto tw:transition-transform ${showTodo ? "tw:rotate-180" : ""}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            {showTodo && (
              <>
                {todoVideos.length === 0 ? (
                  <div className="tw:text-center tw:py-12 tw:bg-gray-50 tw:rounded-xl tw:border tw:border-dashed tw:border-gray-300">
                    <p className="tw:mt-2 tw:text-lg tw:font-medium tw:text-gray-900">Tutti i video completati!</p>
                    <p className="tw:text-sm tw:text-gray-500">Ottimo lavoro, hai terminato tutti i corsi assegnati.</p>
                  </div>
                ) : (
                  <div className="tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:lg:grid-cols-3 tw:gap-6">
                    {todoVideos.map((v) => (
                      <VideoCard key={v.video_id} video={v} onClick={() => handleSelect(v)} isCompleted={false} />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="tw:mt-6"></div>
          {/* Video Completati */}
          {doneVideos.length > 0 && (
            <div>
              <div
                className="tw:p-3 tw:flex tw:items-center tw:gap-3 tw:mb-3 tw:cursor-pointer"
                onClick={() => setShowDone(!showDone)}
              >
                <h2 className="tw:text-xl tw:font-bold tw:text-gray-800">
                  <span className="tw:bg-green-500 tw:text-white tw:mr-2 tw:px-4 tw:py-1 tw:rounded-full tw:text-xl tw:font-bold tw:shadow-sm">
                    {doneVideos.length}
                  </span>
                  Video Completati
                </h2>
                <svg
                  className={`tw:w-6 tw:h-6 tw:ml-auto tw:transition-transform ${showDone ? "tw:rotate-180" : ""}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {showDone && (
                <div className="tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:lg:grid-cols-3 tw:gap-6">
                  {doneVideos.map((v) => (
                    <VideoCard key={v.video_id} video={v} onClick={() => handleSelect(v)} isCompleted={true} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
